import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n, tr } from "../../lib/i18n";
import { Quote, Star, BadgeCheck, ShieldCheck, Award, CreditCard, HeartHandshake } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

export function Testimonials() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/testimonials?limit=6").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="testimonials">
      <div className="max-w-2xl">
        <h2 className="font-serif text-4xl sm:text-5xl">{t("testimonials_title")}</h2>
        <p className="text-stone-600 mt-3 text-sm">{t("testimonials_sub")}</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
        {items.map((r) => (
          <figure key={r.id} data-testid={`testimonial-${r.id}`} className="relative min-w-0 overflow-hidden bg-white border border-stone-200/70 rounded-3xl p-7 flex flex-col">
            <Quote size={28} className="text-stone-200 absolute top-6 right-6" />
            <div className="flex gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}</div>
            <blockquote className="mt-4 text-stone-800 leading-relaxed text-[15px] flex-1">“{tr(r, "comment", lang)}”</blockquote>
            <figcaption className="mt-6 flex items-center gap-3">
              {r.customer?.avatar && <img src={r.customer.avatar} alt={r.customer.name} className="w-10 h-10 rounded-full object-cover" />}
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.customer?.name}<span className="text-stone-400 font-normal"> · {r.customer?.location}</span></div>
                <div className="text-xs text-stone-500 truncate">{t("about_service")} <Link to={`/providers/${r.provider?.id}`} className="underline decoration-stone-300 hover:decoration-stone-900">{r.provider?.name}</Link></div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function StatsBar() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats/public").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const items = [[s.providers, t("stats_providers")], [s.reviews, t("stats_reviews")], [`${s.avg_rating} / 5`, t("stats_rating")], [s.completed_bookings, t("stats_bookings")]];
  return (
    <div data-testid="stats-bar" className="bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map(([v, l]) => (
          <div key={l} className="border-l border-white/15 pl-5"><div className="font-serif text-3xl sm:text-4xl">{v}</div><div className="text-xs uppercase tracking-wider text-white/60 mt-1">{l}</div></div>
        ))}
      </div>
    </div>
  );
}

export function VettedPros({ providers }) {
  const { t, lang } = useI18n();
  const pillars = [[ShieldCheck, "vetted_1"], [Award, "vetted_2"], [CreditCard, "vetted_3"], [HeartHandshake, "vetted_4"]];
  return (
    <section className="relative py-20 overflow-hidden bg-stone-950 text-white" data-testid="vetted-pros">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-12 items-start">
        <div className="lg:col-span-2">
          <div className="eyebrow text-white/50">— {t("verified")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">{t("vetted_title")}</h2>
          <p className="text-white/70 mt-4 text-sm leading-relaxed">{t("vetted_sub")}</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {pillars.map(([Icon, k]) => (
              <div key={k}><Icon size={22} className="text-emerald-300" /><div className="font-medium mt-2">{t(`${k}_t`)}</div><p className="text-white/60 text-xs mt-1 leading-relaxed">{t(`${k}_d`)}</p></div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
          {providers.slice(0, 4).map((p) => (
            <Link key={p.id} to={`/providers/${p.id}`} data-testid={`vetted-pro-${p.id}`} className="group relative rounded-3xl overflow-hidden aspect-[4/5] block">
              <img src={p.cover_image || p.avatar} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1 text-[11px] bg-white text-stone-900 rounded-full px-2.5 py-1 font-medium"><BadgeCheck size={13} className="text-emerald-600" />{t("verified")}</div>
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end gap-3">
                <img src={p.avatar} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white" />
                <div className="min-w-0"><div className="font-serif text-xl leading-tight">{p.name}</div><div className="text-xs text-white/70 truncate">{p.location} · {p.years_experience} {lang === "fr" ? "ans" : "yrs"}</div></div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQ() {
  const { t } = useI18n();
  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20" data-testid="faq">
      <h2 className="font-serif text-4xl sm:text-5xl">{t("faq_title")}</h2>
      <Accordion type="single" collapsible className="mt-8">
        {[1, 2, 3, 4, 5].map((i) => (
          <AccordionItem key={i} value={`q${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-sans text-base font-medium hover:no-underline py-5">{t(`faq_${i}_q`)}</AccordionTrigger>
            <AccordionContent className="text-stone-600 leading-relaxed text-sm font-sans">
              {t(`faq_${i}_a`)}{i === 3 && <> <Link to="/refund-policy" className="underline">{t("legal_refund")}</Link>.</>}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

const PARTNERS = ["French-American Chamber of Commerce", "Alliance Française", "Consulat de France à Miami", "Business France", "French Founders"];

export function Partners() {
  const { t } = useI18n();
  return (
    <section className="border-y border-stone-200 bg-stone-50" data-testid="partners">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-center"><div className="eyebrow text-stone-500">{t("partners_title")}</div><p className="text-xs text-stone-500 mt-1">{t("partners_sub")}</p></div>
        <div className="mt-6 flex flex-wrap justify-center gap-x-10 gap-y-4">
          {PARTNERS.map((p) => <span key={p} className="font-serif text-xl text-stone-500">{p}</span>)}
        </div>
      </div>
    </section>
  );
}
